import React, { useState } from 'react';
import { LoaderCircle, Mail, Send } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BackButton from '../components/BackButton';
import PrivacyNotice from '../components/PrivacyNotice';
import { useTranslations } from '../translations';
import { useNotification } from '../components/NotificationProvider';
import { ApiError } from '../services/api.error';
import { sendContactRequest } from '../services/contact';
import './ContactPage.css';

interface ContactPageProps {
  isLoggedIn: boolean;
  isAdmin?: boolean;
  onNavigateToLogin: () => void;
  onNavigateToRegister: () => void;
  onNavigateToHome: () => void;
  onLogout: () => void;
  onNavigateToProfile?: () => void;
  onNavigateToMyAnnouncements?: () => void;
  onNavigateToLocations?: () => void;
  onNavigateToSettings?: () => void;
  onNavigateToAdministration?: () => void;
  onNavigateToCreateProduct?: () => void;
  onNavigateToMessages?: (productId?: string | null) => void;
  onNavigateToTerms?: () => void;
  onNavigateToPrivacy?: () => void;
  onNavigateToCookies?: () => void;
  onNavigateToAbout?: () => void;
}

const ContactPage: React.FC<ContactPageProps> = ({
  isLoggedIn,
  isAdmin = false,
  onNavigateToLogin,
  onNavigateToRegister,
  onNavigateToHome,
  onLogout,
  onNavigateToProfile,
  onNavigateToMyAnnouncements,
  onNavigateToLocations,
  onNavigateToSettings,
  onNavigateToAdministration,
  onNavigateToCreateProduct,
  onNavigateToMessages,
  onNavigateToTerms,
  onNavigateToPrivacy,
  onNavigateToCookies,
  onNavigateToAbout,
}) => {
  const t = useTranslations();
  const { showNotification } = useNotification();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    website: '',
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateEmail = (email: string): boolean => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = t.contact.requiredField;
    }

    if (!formData.email.trim()) {
      newErrors.email = t.contact.requiredField;
    } else if (!validateEmail(formData.email)) {
      newErrors.email = t.contact.invalidEmail;
    }

    if (!formData.subject.trim()) {
      newErrors.subject = t.contact.requiredField;
    }

    if (!formData.message.trim()) {
      newErrors.message = t.contact.requiredField;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await sendContactRequest({
        name: formData.name.trim(),
        email: formData.email.trim(),
        subject: formData.subject.trim(),
        message: formData.message.trim(),
        website: formData.website,
      });

      showNotification(t.contact.successMessage, 'success');

      // Reset form
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
        website: '',
      });
      setErrors({});
    } catch (error) {
      showNotification(
        error instanceof ApiError && error.status === 429
          ? t.contact.rateLimited
          : t.contact.errorMessage,
        'error'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  return (
    <div className="contact-page">
      <Header
        isLoggedIn={isLoggedIn}
        isAdmin={isAdmin}
        onNavigateToLogin={onNavigateToLogin}
        onNavigateToRegister={onNavigateToRegister}
        onNavigateToHome={onNavigateToHome}
        onLogout={onLogout}
        onProfileClick={onNavigateToProfile}
        onLocationsClick={onNavigateToLocations}
        onMessagesClick={onNavigateToMessages}
        onAnnouncementsClick={onNavigateToMyAnnouncements}
        onSettingsClick={onNavigateToSettings}
        onAdministrationClick={onNavigateToAdministration}
        onNavigateToCreateProduct={onNavigateToCreateProduct}
      />

      <div className="contact-page-content">
        <div className="contact-page-shell">
          <BackButton className="contact-page-back-button" onClick={() => window.history.back()} />

          <div className="contact-page-container">
            <div className="contact-page-intro">
              <span className="contact-page-intro-icon" aria-hidden="true">
                <Mail />
              </span>
              <div className="contact-page-intro-copy">
                <h1 className="contact-page-title">{t.contact.title}</h1>
                <p className="contact-page-subtitle">{t.contact.subtitle}</p>
                <p className="contact-page-subtitle">
                  {t.contact.emailNote}{' '}
                  <a className="contact-page-email" href={`mailto:${t.contact.emailAddress}`}>{t.contact.emailAddress}</a>{' '}
                  {t.contact.phoneNote}{' '}
                  <a className="contact-page-email" href={t.contact.phoneHref}>{t.contact.phoneNumber}</a>
                </p>
              </div>
            </div>

            <form className="contact-page-form" onSubmit={handleSubmit} aria-busy={isSubmitting}>
            <div className="contact-page-honeypot" aria-hidden="true">
              <label htmlFor="website">Website</label>
              <input
                type="text"
                id="website"
                name="website"
                value={formData.website}
                onChange={handleChange}
                autoComplete="off"
                tabIndex={-1}
              />
            </div>
            <div className="contact-page-form-group">
              <label htmlFor="name" className="contact-page-form-label">
                {t.contact.nameLabel}<span className="contact-page-required" aria-hidden="true">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                className={`contact-page-form-input ${errors.name ? 'error' : ''}`}
                placeholder={t.contact.namePlaceholder}
                value={formData.name}
                onChange={handleChange}
                maxLength={100}
                aria-required="true"
                aria-invalid={Boolean(errors.name)}
              />
              {errors.name && <span className="contact-page-form-error">{errors.name}</span>}
            </div>

            <div className="contact-page-form-group">
              <label htmlFor="email" className="contact-page-form-label">
                {t.contact.emailLabel}<span className="contact-page-required" aria-hidden="true">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                className={`contact-page-form-input ${errors.email ? 'error' : ''}`}
                placeholder={t.contact.emailPlaceholder}
                value={formData.email}
                onChange={handleChange}
                maxLength={254}
                aria-required="true"
                aria-invalid={Boolean(errors.email)}
              />
              {errors.email && <span className="contact-page-form-error">{errors.email}</span>}
            </div>

            <div className="contact-page-form-group contact-page-form-group-wide">
              <label htmlFor="subject" className="contact-page-form-label">
                {t.contact.subjectLabel}<span className="contact-page-required" aria-hidden="true">*</span>
              </label>
              <input
                type="text"
                id="subject"
                name="subject"
                className={`contact-page-form-input ${errors.subject ? 'error' : ''}`}
                placeholder={t.contact.subjectPlaceholder}
                value={formData.subject}
                onChange={handleChange}
                maxLength={150}
                aria-required="true"
                aria-invalid={Boolean(errors.subject)}
              />
              {errors.subject && <span className="contact-page-form-error">{errors.subject}</span>}
            </div>

            <div className="contact-page-form-group contact-page-form-group-wide">
              <label htmlFor="message" className="contact-page-form-label">
                {t.contact.messageLabel}<span className="contact-page-required" aria-hidden="true">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                className={`contact-page-form-textarea ${errors.message ? 'error' : ''}`}
                placeholder={t.contact.messagePlaceholder}
                value={formData.message}
                onChange={handleChange}
                rows={6}
                maxLength={5000}
                aria-required="true"
                aria-invalid={Boolean(errors.message)}
              />
              {errors.message && <span className="contact-page-form-error">{errors.message}</span>}
            </div>

            <PrivacyNotice onNavigateToPrivacy={onNavigateToPrivacy} />

            <button
              type="submit"
              className="contact-page-submit-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <LoaderCircle className="contact-page-submit-spinner" aria-hidden="true" />
              ) : (
                <Send aria-hidden="true" />
              )}
              {isSubmitting ? t.contact.submitting : t.contact.submitButton}
            </button>
          </form>
          </div>
        </div>
      </div>

      <Footer
        onNavigateToTerms={onNavigateToTerms}
        onNavigateToPrivacy={onNavigateToPrivacy}
        onNavigateToCookies={onNavigateToCookies}
        onNavigateToAbout={onNavigateToAbout}
      />

    </div>
  );
};

export default ContactPage;
